import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  MoreVertical,
  Truck,
  Printer,
  Eye,
  Edit,
  Trash2,
  Package,
  AlertTriangle,
  ArrowRight
} from "lucide-react";

const LocalPurchaseOrders = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedLPO, setSelectedLPO] = useState<any>(null);
  const [items, setItems] = useState([
    { id: 1, name: "", quantity: 1, unitPrice: 0, total: 0 }
  ]);

  // Mock LPO data
  const lpoList = [
    {
      id: "LPO-2024-001",
      date: "2024-03-10",
      supplier: "ABC Office Supplies",
      deliveryLocation: "Main Store",
      items: [
        { name: "Office Chairs", quantity: 20, unitPrice: 4500, total: 90000 },
        { name: "Desks", quantity: 15, unitPrice: 8000, total: 120000 }
      ],
      subtotal: 210000,
      discount: 10000,
      tax: 0,
      total: 200000,
      expectedDelivery: "2024-03-20",
      status: "approved",
      deliveryStatus: "delivered",
      deliveryDate: "2024-03-19",
      department: "Administration",
      budgetBalance: 500000,
      quotationRef: "QT-2024-ABC-001",
      createdBy: "Procurement Officer",
      convertedToPV: true,
      pvNumber: "PV-2024-001"
    },
    {
      id: "LPO-2024-002",
      date: "2024-03-15",
      supplier: "Tech Distributors Ltd",
      deliveryLocation: "Computer Lab",
      items: [
        { name: "Desktop Computers", quantity: 30, unitPrice: 35000, total: 1050000 },
        { name: "Monitors", quantity: 30, unitPrice: 12000, total: 360000 },
        { name: "Keyboards & Mice", quantity: 30, unitPrice: 2000, total: 60000 }
      ],
      subtotal: 1470000,
      discount: 70000,
      tax: 0,
      total: 1400000,
      expectedDelivery: "2024-03-30",
      status: "approved",
      deliveryStatus: "partially_delivered",
      deliveryDate: null,
      department: "ICT",
      budgetBalance: 2000000,
      budgetExceeded: false,
      quotationRef: "QT-2024-TECH-045",
      createdBy: "HOD ICT",
      convertedToPV: false,
      pvNumber: null
    },
    {
      id: "LPO-2024-003",
      date: "2024-03-18",
      supplier: "Books & More Publishers",
      deliveryLocation: "Library",
      items: [
        { name: "Textbooks - Mathematics", quantity: 100, unitPrice: 800, total: 80000 },
        { name: "Reference Books - Science", quantity: 50, unitPrice: 1200, total: 60000 }
      ],
      subtotal: 140000,
      discount: 5000,
      tax: 0,
      total: 135000,
      expectedDelivery: "2024-04-05",
      status: "pending",
      deliveryStatus: "not_delivered",
      deliveryDate: null,
      department: "Academic",
      budgetBalance: 120000,
      budgetExceeded: true,
      quotationRef: "QT-2024-BOOKS-012",
      createdBy: "Librarian",
      convertedToPV: false,
      pvNumber: null
    }
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
          <CheckCircle className="h-3 w-3 mr-1" />
          Approved
        </Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
          <XCircle className="h-3 w-3 mr-1" />
          Rejected
        </Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getDeliveryStatusBadge = (status: string) => {
    switch (status) {
      case 'delivered':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
          <Package className="h-3 w-3 mr-1" />
          Delivered
        </Badge>;
      case 'partially_delivered':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
          <Truck className="h-3 w-3 mr-1" />
          Partial
        </Badge>;
      case 'not_delivered':
        return <Badge variant="outline">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const addItem = () => {
    setItems([...items, { id: items.length + 1, name: "", quantity: 1, unitPrice: 0, total: 0 }]);
  };

  const removeItem = (id: number) => {
    setItems(items.filter(item => item.id !== id));
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + item.total, 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h2 className="text-2xl font-bold">Local Purchase Orders (LPOs)</h2>
          <p className="text-sm text-muted-foreground">Manage purchase orders for goods and supplies</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create LPO
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Local Purchase Order</DialogTitle>
                <DialogDescription>Issue a purchase order for goods and supplies</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="lpo-date">Date</Label>
                    <Input id="lpo-date" type="date" defaultValue={new Date().toISOString().split('T')[0]} />
                  </div>
                  <div>
                    <Label htmlFor="lpo-number">LPO Number</Label>
                    <Input id="lpo-number" value="LPO-2024-004" disabled />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="supplier">Supplier/Vendor</Label>
                    <Input id="supplier" placeholder="Select or enter supplier name" />
                  </div>
                  <div>
                    <Label htmlFor="delivery-location">Delivery Location</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="main-store">Main Store</SelectItem>
                        <SelectItem value="computer-lab">Computer Lab</SelectItem>
                        <SelectItem value="library">Library</SelectItem>
                        <SelectItem value="admin-office">Admin Office</SelectItem>
                        <SelectItem value="science-lab">Science Lab</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="department">Department</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administration</SelectItem>
                        <SelectItem value="academic">Academic</SelectItem>
                        <SelectItem value="ict">ICT</SelectItem>
                        <SelectItem value="science">Science</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="expected-delivery">Expected Delivery Date</Label>
                    <Input id="expected-delivery" type="date" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="quotation-ref">Quotation Reference</Label>
                  <Input id="quotation-ref" placeholder="QT-2024-XXX-XXX" />
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-base font-semibold">Items</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addItem}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </Button>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[40%]">Item Name</TableHead>
                          <TableHead className="w-[15%]">Quantity</TableHead>
                          <TableHead className="w-[20%]">Unit Price</TableHead>
                          <TableHead className="w-[20%]">Total</TableHead>
                          <TableHead className="w-[5%]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Input placeholder="Item description" className="h-9" />
                            </TableCell>
                            <TableCell>
                              <Input type="number" defaultValue={1} className="h-9" />
                            </TableCell>
                            <TableCell>
                              <Input type="number" placeholder="0.00" className="h-9" />
                            </TableCell>
                            <TableCell>
                              <Input value="0.00" disabled className="h-9" />
                            </TableCell>
                            <TableCell>
                              {items.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeItem(item.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span className="font-medium">Ksh {calculateTotal().toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span>Discount:</span>
                    <Input type="number" placeholder="0.00" className="w-32 h-8 text-right" />
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span>Tax (if applicable):</span>
                    <Input type="number" placeholder="0.00" className="w-32 h-8 text-right" />
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Total:</span>
                    <span>Ksh {calculateTotal().toLocaleString()}</span>
                  </div>
                </div>

                <div>
                  <Label>Budget Check</Label>
                  <div className="mt-2 p-3 bg-muted rounded-lg flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Available Budget: Ksh 500,000</p>
                      <p className="text-xs text-muted-foreground">After this LPO: Ksh 300,000</p>
                    </div>
                    <Badge variant="outline" className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                      Within Budget
                    </Badge>
                  </div>
                </div>

                <div>
                  <Label htmlFor="terms">Terms & Conditions</Label>
                  <Textarea id="terms" placeholder="Payment terms, delivery conditions, etc." rows={3} />
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button variant="outline">Save as Draft</Button>
                  <Button onClick={() => setShowCreateDialog(false)}>Submit for Approval</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by LPO number, supplier, or items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* LPO Table */}
      <Card>
        <CardHeader>
          <CardTitle>Purchase Order Register</CardTitle>
          <CardDescription>All local purchase orders and their status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>LPO Number</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Total Amount</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Approval Status</TableHead>
                  <TableHead>Delivery Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lpoList.map((lpo) => (
                  <TableRow key={lpo.id}>
                    <TableCell className="font-medium">{lpo.id}</TableCell>
                    <TableCell>{lpo.date}</TableCell>
                    <TableCell>{lpo.supplier}</TableCell>
                    <TableCell className="font-bold">
                      Ksh {lpo.total.toLocaleString()}
                      {lpo.budgetExceeded && (
                        <AlertTriangle className="inline h-4 w-4 ml-1 text-destructive" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{lpo.department}</Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(lpo.status)}</TableCell>
                    <TableCell>{getDeliveryStatusBadge(lpo.deliveryStatus)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setSelectedLPO(lpo);
                            setShowDetailsDialog(true);
                          }}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Printer className="h-4 w-4 mr-2" />
                            Print LPO
                          </DropdownMenuItem>
                          {lpo.deliveryStatus === 'delivered' && !lpo.convertedToPV && (
                            <DropdownMenuItem>
                              <ArrowRight className="h-4 w-4 mr-2" />
                              Convert to PV
                            </DropdownMenuItem>
                          )}
                          {lpo.deliveryStatus === 'not_delivered' && (
                            <DropdownMenuItem>
                              <Package className="h-4 w-4 mr-2" />
                              Mark as Delivered
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem className="text-destructive">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Cancel LPO
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>LPO Details</DialogTitle>
            <DialogDescription>{selectedLPO?.id}</DialogDescription>
          </DialogHeader>
          {selectedLPO && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Supplier</Label>
                  <p className="font-medium">{selectedLPO.supplier}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Delivery Location</Label>
                  <p className="font-medium">{selectedLPO.deliveryLocation}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Expected Delivery</Label>
                  <p className="font-medium">{selectedLPO.expectedDelivery}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Quotation Reference</Label>
                  <p className="font-medium">{selectedLPO.quotationRef}</p>
                </div>
              </div>

              <div>
                <Label className="text-lg font-semibold">Items Ordered</Label>
                <Table className="mt-3">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Unit Price</TableHead>
                      <TableHead>Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedLPO.items.map((item: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>Ksh {item.unitPrice.toLocaleString()}</TableCell>
                        <TableCell className="font-medium">Ksh {item.total.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="mt-4 space-y-2 text-right">
                  <div className="flex justify-end gap-4">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="font-medium w-32">Ksh {selectedLPO.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-end gap-4">
                    <span className="text-muted-foreground">Discount:</span>
                    <span className="font-medium w-32">- Ksh {selectedLPO.discount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-end gap-4 pt-2 border-t">
                    <span className="font-bold">Total:</span>
                    <span className="font-bold w-32">Ksh {selectedLPO.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {selectedLPO.convertedToPV && (
                <div className="p-3 bg-green-100 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <Label className="font-semibold text-green-800 dark:text-green-400">Converted to Payment Voucher</Label>
                  <p className="text-sm mt-1">This LPO has been converted to {selectedLPO.pvNumber}</p>
                </div>
              )}

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline">
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
                {selectedLPO.deliveryStatus === 'delivered' && !selectedLPO.convertedToPV && (
                  <Button>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Convert to Payment Voucher
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocalPurchaseOrders;